import { MetaFunction } from "@remix-run/node"
import {
  articleBodyStyle,
  articleCompareImages,
  articleImageStyle,
  bold,
  figCaptionStyle,
  figCaptionStyle2,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  infoWrap,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import StickyHeader from "~/components/StickyHeader"
import Footer from "~/components/Footer"
import LinkButton from "~/components/LinkButton"
import Logo from "~/components/Logo"
import Link from "~/components/Link"
import { page } from "~/shared/styles.css"
import { golden } from "~/components/styles/theme.css"

const TITLE = "Best Plants to Grow in U.S. Hardiness Zones | Garden Guide"
const DESCRIPTION =
  "Discover the top trees, shrubs, and perennials for your US hardiness zone. Find plants that thrive in your climate with expert tips and advice."
const URL =
  "https://rescapeai.com/article/choosing-plants-suitable-for-different-us-hardiness-zones"
const IMAGE = "https://rescapeai.com/articles/hardiness-zones.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: URL,
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-22",
        dateModified: "2025-10-22",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": URL,
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/articles/achillea_millefolium.webp)",
          backgroundPosition: "0% 40%",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            Best Plants to Grow in Different U.S. Hardiness Zones
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Understanding Hardiness Zones</h2>
          <p className={articleBodyStyle}>
            The USDA Hardiness Zones map the United States based on{" "}
            <strong className={bold}>average annual minimum winter temperatures</strong>.
            They range from <strong className={bold}>Zone 1</strong> (coldest) to{" "}
            <strong className={bold}>Zone 13</strong> (warmest). Knowing your zone helps
            you select plants that can survive your winter temperatures. While winter
            hardiness is important, remember that summer heat, humidity, soil type, and
            drainage also affect plant success. See what Rescape AI can do—and how it can
            generate <Link href="/ai-yard-design">yard design images here</Link>{" "}
            based on your style—<Link href="/hello/signup">Sign Up Here</Link> or learn
            more about Hardiness Zones by reading on.
          </p>
          <p className={articleBodyStyle}>
            Here’s a quick reference for the zones we’ll focus on:
          </p>
          <ul className={unorderedList} style={{ lineHeight: "2rem" }}>
            <li>
              <strong className={bold}>6 |</strong> −10°F to 0°F (−23°C to −18°C)
            </li>
            <li>
              <strong className={bold}>7 |</strong> 0°F to 10°F (−18°C to −12°C)
            </li>
            <li>
              <strong className={bold}>8 |</strong> 10°F to 20°F (−12°C to −7°C)
            </li>
            <li>
              <strong className={bold}>9+ |</strong> 20°F+ (−7°C and warmer)
            </li>
          </ul>
          <p className={articleBodyStyle}>
            With this context, let’s explore plant recommendations for each zone,
            emphasizing what thrives in your climate rather than focusing solely on cold
            tolerance.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Zone 6: Cold-Winter Gardens</h2>
          <p className={articleBodyStyle}>
            Zone 6 experiences cold winters, so plants need to withstand frost but can
            also handle a moderately long growing season.
          </p>
          <figure>
            <img
              src="/articles/hibiscus.webp"
              className={articleImageStyle}
              alt="Beautiful purple Hibiscus in bloom"
              style={{
                marginTop: `${golden * 0.5}rem`,
              }}
            />
            <figcaption className={figCaptionStyle}>A purple blue Hibiscus</figcaption>
          </figure>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 6: Trees</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Cornus kousa (Kousa dogwood)</strong> – Stunning
                spring blooms and fall foliage.
              </li>
              <li>
                <strong className={bold}>Cercis canadensis (Eastern redbud)</strong> –
                Early spring pink flowers brighten the garden.
              </li>
            </ul>
            <div className={articleCompareImages}>
              <figure>
                <img src={"/articles/dogwood.webp"} alt={"Dogwood Tree"} loading="lazy" />
                <figcaption className={figCaptionStyle2}>Kousa Dogwood Tree</figcaption>
              </figure>
              <figure>
                <img
                  src={"/articles/eastern-redbud.webp"}
                  alt={"Eastern Redbud Tree in Summer"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Eastern Redbud Tree</figcaption>
              </figure>
            </div>
          </section>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 6: Shrubs</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Buxus. (Boxwood)</strong> – Evergreen structure
                and winter interest.
              </li>
              <li>
                <strong className={bold}>Hibiscus syriacus (Rose of Sharon)</strong> –
                Summer blooms and long flowering season.
              </li>
              <li>
                <strong className={bold}>Spiraea japonica (Japanese spirea)</strong> –
                Easy-care flowering shrub with vibrant colors.
              </li>
            </ul>
            <div className={articleCompareImages}>
              <figure>
                <img
                  src={"/articles/boxwood.webp"}
                  alt={"Boxwood shrub"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Boxwood</figcaption>
              </figure>
              <figure>
                <img
                  src={"/articles/japanese-spirea.webp"}
                  alt={"Japanese Spirea"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Japanese Spirea</figcaption>
              </figure>
            </div>
          </section>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 6: Perennials</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Hostas</strong> – Shade-loving foliage with
                elegant flowers.
              </li>
              <li>
                <strong className={bold}>Achillea millefolium (Yarrow)</strong> –
                Drought-tolerant with long-lasting blooms.
              </li>
              <li>
                <strong className={bold}>Hemerocallis (Daylillies)</strong> –
                Low-maintenance flowers that return every year.
              </li>
            </ul>
            <p className={articleBodyStyle}>
              <strong className={bold}>Tip:</strong> Plant in spring or fall for best root
              establishment. Use mulch for winter protection and combine shrubs,
              perennials, and evergreens for multi-season interest.
            </p>
            <div className={articleCompareImages}>
              <figure>
                <img
                  src={"/articles/hostas.webp"}
                  alt={"Flowering Hosta"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Hosta</figcaption>
              </figure>
              <figure>
                <img
                  src={"/articles/hemerocallis.webp"}
                  alt={"Hemerocallis flower"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Hemerocallis</figcaption>
              </figure>
            </div>
          </section>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Zone 7: Transitional Gardens</h2>
          <figure>
            <img
              src="/articles/autumn-leaves.webp"
              className={articleImageStyle}
              alt="Reddening Autumn Leaves"
              style={{
                marginTop: `${golden * 0.5}rem`,
              }}
            />
            <figcaption className={figCaptionStyle}>
              Zone 7 has milder winters, giving gardeners more flexibility. Many plants
              that are borderline hardy in colder zones will thrive here.
            </figcaption>
          </figure>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 7: Trees</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Acer rubrum (Red maple)</strong> – Brilliant fall
                colors and adaptable growth.
              </li>
              <li>
                <strong className={bold}>Magnolia grandiflora (Southern magnolia)</strong>{" "}
                – Iconic evergreen with fragrant flowers.
              </li>
            </ul>
            <div className={articleCompareImages}>
              <figure>
                <img
                  src={"/articles/acer-rubrum.webp"}
                  alt={"Acer Rubrum Leaves"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Acer Rubrum</figcaption>
              </figure>
              <figure>
                <img
                  src={"/articles/southern-magnolia.webp"}
                  alt={"Southern Magnolia Flowers"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Southern Magnolia</figcaption>
              </figure>
            </div>
          </section>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 7: Shrubs</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Gardenia jasminoides</strong> – Fragrant white
                flowers; choose “hardy” cultivars.
              </li>
              <li>
                <strong className={bold}>Rhododendron</strong> – Acid-loving, vibrant
                blooms in spring.
              </li>
              <li>
                <strong className={bold}>Nerium oleander</strong> – Hardy in sheltered
                spots, great for warmer microclimates.
              </li>
            </ul>
            <div className={articleCompareImages}>
              <figure>
                <img
                  src={"/articles/rhododendron.webp"}
                  alt={"Rhododendron"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Rhododendron</figcaption>
              </figure>
              <figure>
                <img
                  src={"/articles/gardenia-jasminoides.webp"}
                  alt={"Gardenia Jasminoides"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Gardenia Jasminoides</figcaption>
              </figure>
            </div>
          </section>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 7: Perennials & Annuals</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Lavandula (Lavender)</strong> – Fragrant,
                drought-tolerant, attracts pollinators.
              </li>
              <li>
                <strong className={bold}>Phlox</strong> – Bright summer blooms, works in
                beds or borders.
              </li>
              <li>
                <strong className={bold}>Tagetes (Marigolds)</strong> – Easy annuals for
                color and pest control.
              </li>
            </ul>
            <div className={articleCompareImages}>
              <figure>
                <img src={"/articles/phlox.webp"} alt={"Phlox"} loading="lazy" />
                <figcaption className={figCaptionStyle}>Phlox</figcaption>
              </figure>
              <figure>
                <img
                  src={"/articles/tagetes.webp"}
                  alt={"Tagetes (Marigolds)"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle}>Tagetes (Marigolds)</figcaption>
              </figure>
            </div>
          </section>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Zone 8: Warm-Winter Gardens</h2>
          <figure>
            <img
              src="/articles/nerium-oleander.webp"
              className={articleImageStyle}
              alt="Nerium Oleander"
              style={{
                marginTop: `${golden * 0.5}rem`,
              }}
            />
            <figcaption className={figCaptionStyle}>
              Zone 8 enjoys mild winters and long growing seasons, ideal for both native
              and subtropical plants. The main challenge is summer heat, so choose
              heat-tolerant species.
            </figcaption>
          </figure>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 8: Trees</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Quercus virginiana (Southern live oak)</strong> –
                Evergreen shade tree, iconic southern species.
              </li>
              <li>
                <strong className={bold}>Taxodium distichum (Bald cypress)</strong> –
                Thrives in moist soils, excellent for wetlands.
              </li>
            </ul>
            <div className={articleCompareImages}>
              <figure>
                <img
                  src={"/articles/southern-live-oak.webp"}
                  alt={"Southern Live Oak"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle}>Southern Live Oak</figcaption>
              </figure>
              <figure>
                <img
                  src={"/articles/bald-cypress.webp"}
                  alt={"Bald Cypress"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle}>Bald Cypress</figcaption>
              </figure>
            </div>
          </section>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 8: Shrubs</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Rhododendron indica (Indian hawthorn)</strong> –
                Compact shrub with pink blooms.
              </li>
              <li>
                <strong className={bold}>Gardenia jasminoides (Cape Jasmin)</strong> –
                Works well in warm winters for fragrant blooms.
              </li>
              <li>
                <strong className={bold}>Nerium oleander</strong> – Tolerates heat and
                poor soils, blooms almost all summer.
              </li>
            </ul>
            <div className={articleCompareImages}>
              <figure>
                <img
                  src={"/articles/indian-hawthorn.webp"}
                  alt={"Rhododendron indica"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Indian Hawthorn</figcaption>
              </figure>
              <figure>
                <img
                  src={"/articles/gardenia-jasminoides-2.webp"}
                  alt={"Gardenia Jasminoides"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Cape Jasmin</figcaption>
              </figure>
            </div>
          </section>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 8: Perennials & Annuals</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Lavandula (Lavender)</strong> – Excellent for
                borders and pollinator gardens. And Cottage Gardens{" "}
                {/**COTTAGE GARDEN AI LINK */}
              </li>
              <li>
                <strong className={bold}>Achillea millefolium (Yarrow)</strong> –
                Tolerates heat and low water.
              </li>
              <li>
                <strong className={bold}>Vinca (Catharanthus roseus)</strong> – Colorful
                annual for sunny spots.
              </li>
            </ul>
            <p className={articleBodyStyle}>
              <strong className={bold}>Tip:</strong> Use mulch to protect soil moisture
              during long summers, and plant in areas with some shade if afternoon sun is
              intense. Containers allow you to move tender species during extreme heat.
            </p>
            <div className={articleCompareImages}>
              <figure>
                <img src={"/articles/vinca.webp"} alt={"Vinca Flowers"} loading="lazy" />
                <figcaption className={figCaptionStyle2}>Vinca Flowers</figcaption>
              </figure>
              <figure>
                <img src={"/articles/lavender.webp"} alt={"Lavender"} loading="lazy" />
                <figcaption className={figCaptionStyle2}>Lavender</figcaption>
              </figure>
            </div>
          </section>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Zone 9+: Subtropical & Tropical-Friendly Gardens</h2>
          <figure>
            <img
              src="/articles/ixora-coccinea-flower.webp"
              className={articleImageStyle}
              alt="Ixora Coccinea"
              style={{
                marginTop: `${golden * 0.5}rem`,
              }}
            />
            <figcaption className={figCaptionStyle}>
              Zones 9 and above experience very mild winters, allowing you to grow
              subtropical or tropical plants. Summer heat and drought tolerance become
              more important than winter hardiness. {/** Link to Meditteranean ai page */}
            </figcaption>
          </figure>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 9+: Trees</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Jacaranda mimosifolia</strong> – Striking purple
                blooms in late spring.
              </li>
              <li>
                <strong className={bold}>Citrus trees</strong> – Lemon, orange, lime, and
                grapefruit thrive outdoors.
              </li>
            </ul>
            <div className={articleCompareImages}>
              <figure>
                <img
                  src={"/articles/jacaranda-mimosifolia.webp"}
                  alt={"Jacaranda Mimosifolia"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>
                  Jacaranda Mimosifolia
                </figcaption>
              </figure>
              <figure>
                <img
                  src={"/articles/citrus-tree.webp"}
                  alt={"Citrus Tree"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Citrus Tree</figcaption>
              </figure>
            </div>
          </section>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 9+: Shrubs</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Hibiscus rosa-sinensis</strong> – Large tropical
                blooms, ideal in sunny spots.
              </li>
              <li>
                <strong className={bold}>Bougainvillea</strong> – Vibrant flowers,
                drought-tolerant once established.
              </li>
              <li>
                <strong className={bold}>Gardenia jasminoides</strong> – Fragrant blooms,
                ideal in warm climates.
              </li>
            </ul>
            <div className={articleCompareImages}>
              <figure>
                <img
                  src={"/articles/bougainvillea.webp"}
                  alt={"Bougainvillea"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>Bougainvillea</figcaption>
              </figure>
              <figure>
                <img
                  src={"/articles/hibiscus-rosa-sinensis.webp"}
                  alt={"Hibiscus rosa-sinensis"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle2}>
                  Hibiscus rosa-sinensiss
                </figcaption>
              </figure>
            </div>
          </section>
          <section className={stepSectionStyle}>
            <h3 className={h3Style}>Zone 9+: Perennials & Annuals</h3>
            <ul className={unorderedList}>
              <li>
                <strong className={bold}>Plumbago auriculata</strong> – Blue flowering
                perennial climber.
              </li>
              <li>
                <strong className={bold}>Ixora coccinea</strong> – Heat-tolerant flowering
                shrub.
              </li>
              <li>
                <strong className={bold}>Tropicals in containers</strong> – Cannas,
                gingers, and annual coleus for seasonal color.
              </li>
            </ul>
            <p className={articleBodyStyle}>
              <strong className={bold}>Tip:</strong> Even in warm climates, consider
              irrigation and mulching to prevent summer stress. Position plants for wind
              protection and good airflow to avoid fungal issues.
            </p>
            <div className={articleCompareImages}>
              <figure>
                <img
                  src={"/articles/plumbago-auriculata.webp"}
                  alt={"Plumbago auriculata"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle}>Plumbago auriculata</figcaption>
              </figure>
              <figure>
                <img
                  src={"/articles/ixora-coccinea-greenhouse.webp"}
                  alt={"Ixora coccinea"}
                  loading="lazy"
                />
                <figcaption className={figCaptionStyle}>Ixora coccinea</figcaption>
              </figure>
            </div>
          </section>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Conclusion</h2>
          <p className={articleBodyStyle}>
            Choosing plants based on{" "}
            <strong className={bold}>zone-specific recommendations</strong> helps ensure
            your garden thrives. While knowing your hardiness zone is important, it’s
            equally essential to consider sun exposure, soil, moisture, and summer heat.
          </p>
          <ul className={unorderedList}>
            <li>
              <strong className={bold}>Zone 6:</strong> Focus on hardy trees, shrubs, and
              perennials for cold winters.
            </li>
            <li>
              <strong className={bold}>Zone 7:</strong> Expand options to borderline
              tender plants, flowering shrubs, and versatile perennials.
            </li>
            <li>
              <strong className={bold}>Zone 8:</strong> Introduce subtropical species,
              heat-tolerant shrubs, and long-season bloomers.
            </li>
            <li>
              <strong className={bold}>Zone 9+:</strong> Enjoy subtropical and tropical
              plants, citrus trees, and vibrant flowering perennials.
            </li>
          </ul>
          <p className={articleBodyStyle}>
            By combining these plant recommendations with proper site preparation,
            mulching, and watering, your garden can flourish in any U.S. climate.
          </p>
        </section>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
